'use client';

import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { BaseLinePlay } from '@/app/components/icons/BaseLinePlay';
import { Loading } from '@/app/components/icons/Loading';
import type { AgentMediaItem } from '@/app/types';
import { cn } from '@/app/utils';

const renderContent = (item: AgentMediaItem) => {
  // 上传中状态
  if (item.status === 'uploading') {
    return <Loading className="animate-spin text-system-white text-xl" />;
  }

  if (item.type === 'image' && (item.cover_url || item.url)) {
    return (
      <img
        src={item.cover_url || item.url}
        alt="media item"
        className="drag-none size-full rounded-8 border border-system-white24 object-cover"
        loading="lazy"
        decoding="async"
        draggable={false}
      />
    );
  }
  if (item.type === 'video' && item.cover_url) {
    return (
      <div className="relative size-full overflow-hidden rounded-8 border border-system-white24">
        <img
          src={item.cover_url}
          alt={item.type}
          className="drag-none size-full object-cover"
          loading="lazy"
          decoding="async"
          draggable={false}
        />
        {/* 播放按钮 */}
        <div className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 flex size-5 cursor-pointer items-center justify-center rounded-full bg-system-black/40 backdrop-blur-[5px]">
          <BaseLinePlay className="size-[7px]" />
        </div>
      </div>
    );
  }
};

interface UploadedFilesPreviewProps {
  /**
   * 上传文件列表
   */
  uploadList: AgentMediaItem[];
  /**
   * 删除文件回调（可选）
   * - 传入时：显示删除按钮，点击后调用回调
   * - 不传入时：只读模式，不显示删除按钮（用于历史消息展示）
   */
  onRemove?: (id: string) => void;
  /**
   * 自定义类名
   */
  className?: string;
  onPreview?: (items: AgentMediaItem[], activeId?: string) => void;
  showAllOnHover?: boolean;
  /**
   * 自动释放对象 URL
   * - 默认不释放
   * - 如果为 true，则会在删除文件时自动释放对象 URL
   */
  autoRevokeObjectURL?: boolean;
}

/**
 * 文件预览组件
 * 按顺序显示图片和视频（混排）
 *
 * **使用场景：**
 * 1. 输入框上传预览（可编辑）- 传入 `onRemove` 显示删除按钮
 * 2. 历史消息展示（只读）- 不传 `onRemove`，只读模式
 */
export function UploadedFilesPreview({
  uploadList,
  onRemove,
  className,
  showAllOnHover,
  onPreview,
  autoRevokeObjectURL = false,
}: UploadedFilesPreviewProps) {
  if (uploadList.length === 0) return null;

  const isStackedMode = uploadList.length > 3;
  const isEditable = !!onRemove; // 是否可编辑（有删除按钮）

  // 获取旋转角度
  const getRotation = (index: number) => {
    if (!isStackedMode) return 0;
    if (index === 0) return -4;
    if (index === 2) return 4;
    return 0;
  };

  if (isStackedMode) {
    // 叠加模式：当上传列表大于3时
    return (
      <div
        className={cn('group/list relative h-14 cursor-pointer', className)}
        style={{ width: '56px' }}
        onMouseEnter={
          showAllOnHover
            ? (e) => {
                Array.from(e.currentTarget.children).forEach((child, i) => {
                  // 跳过数量标识元素
                  if ((child as HTMLElement).classList.contains('count-badge')) return;
                  const childOffset = i * 48;
                  (child as HTMLElement).style.transform = `translateX(${childOffset}px)`;
                });
              }
            : undefined
        }
        onMouseLeave={
          showAllOnHover
            ? (e) => {
                Array.from(e.currentTarget.children).forEach((child, i) => {
                  // 跳过数量标识元素
                  if ((child as HTMLElement).classList.contains('count-badge')) return;
                  const childRotation = getRotation(i);
                  (child as HTMLElement).style.transform = `rotate(${childRotation}deg) translateX(0px)`;
                });
              }
            : undefined
        }
      >
        {uploadList.map((item, index) => {
          const rotation = getRotation(index);
          return (
            <div
              key={item.tempId || item.id}
              className="group absolute top-0 left-0 flex size-14 items-center justify-center rounded-8 bg-system-white08 transition-transform duration-300 ease-out"
              style={{
                zIndex: uploadList.length - index,
                transform: `rotate(${rotation}deg)`,
              }}
              onClick={() => {
                onPreview?.(uploadList, item.id);
              }}
            >
              {renderContent(item)}
              {/* 删除按钮 - 仅在可编辑模式下显示 */}
              {isEditable && item.status === 'done' && (
                <div
                  className="-right-1.5 -top-1.5 absolute hidden size-5 cursor-pointer items-center justify-center rounded-full bg-system-black/40 backdrop-blur-[50px] group-hover:flex"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(item.id);
                    if (autoRevokeObjectURL && item.url) {
                      URL.revokeObjectURL(item.url);
                    }
                  }}
                >
                  <BaseLineClose className="size-2.5" />
                </div>
              )}
            </div>
          );
        })}
        {/* 数量标识 - hover 时隐藏 */}
        <div
          className={cn(
            'count-badge -right-1.5 -top-1.5 absolute flex size-5 items-center justify-center rounded-full bg-system-black/40 font-medium text-[10px] text-system-white backdrop-blur-[50px]',
            showAllOnHover && 'group-hover/list:hidden',
          )}
          style={{ zIndex: uploadList.length + 1 }}
        >
          {uploadList.length}
        </div>
      </div>
    );
  }

  // 普通模式：正常横向排列
  return (
    <div className={cn('flex flex-wrap gap-2', className)}>
      {uploadList.map((item) => {
        // 图片和视频预览
        return (
          <div
            key={item.tempId || item.id}
            className="group relative flex size-14 items-center justify-center rounded-8 bg-system-white08"
            onClick={() => {
              onPreview?.(uploadList, item.id);
            }}
          >
            {renderContent(item)}
            {/* 删除按钮 - 仅在可编辑模式下显示 */}
            {isEditable && item.status === 'done' && (
              <div
                className="-right-1.5 -top-1.5 absolute hidden size-5 cursor-pointer items-center justify-center rounded-full bg-system-black/40 backdrop-blur-[50px] group-hover:flex"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(item.id);
                  if (autoRevokeObjectURL && item.url) {
                    URL.revokeObjectURL(item.url);
                  }
                }}
              >
                <BaseLineClose className="size-2.5" />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
